import { computed, type ComputedRef, type Ref } from 'vue'
import { initialMoney, toNumber } from '@/modules/shared/utils/money'

/**
 * Computes chart insight values from transactions.
 * Expects pre-filtered transactions (e.g. by shareholder or table filters).
 * Internally excludes loss_investment and portfolio_distribution types.
 * Also surfaces entity-level chart values (commitmentRemaining, hurdleRemaining,
 * investableCapital) so the page doesn't need to re-derive them.
 */
export function useChartInsights(
  rawTransactions: ComputedRef<any[]> | Ref<any[]>,
  entity: ComputedRef<any> | Ref<any>,
) {
  const transfers = computed(() =>
    (rawTransactions.value ?? []).filter((t) => t.type !== 'loss_investment' && t.type !== 'portfolio_distribution'),
  )

  // 3. Sent transfers without ownership transfers (for call metrics)
  const sentTransfers = computed(() => transfers.value.filter((t) => t.sent_at && !Number(t.is_ownership_transfer)))

  // 4. Call metrics from sentTransfers
  const calledManagementFees = computed(() => sumAmount(sentTransfers.value, 'management_fee'))
  const calledOtherFee = computed(() => sumAmount(sentTransfers.value, 'other_fee'))
  const calledSyndicationFee = computed(() => sumAmount(sentTransfers.value, 'syndication_fee'))
  const calledOperatingExpense = computed(() => sumAmount(sentTransfers.value, 'operating_expense'))
  const calledOtherFees = computed(() => calledOtherFee.value + calledSyndicationFee.value)
  const calledOperatingIncome = computed(() => sumAmount(sentTransfers.value, 'operating_income'))
  const calledSettledTotal = computed(() => sumAmountWhere(sentTransfers.value, 'capital_call', 'paid'))
  const calledPendingTotal = computed(() => sumAmountWhere(sentTransfers.value, 'capital_call', 'unpaid'))
  const calledTotal = computed(() => calledSettledTotal.value + calledPendingTotal.value)
  const interestAllocationPaid = computed(() => sumAmount(sentTransfers.value, 'interest_allocation_paid'))
  const interestAllocationReceived = computed(() => sumAmount(sentTransfers.value, 'interest_allocation_received'))
  const calledRecycledFunds = computed(() => sumAmount(sentTransfers.value, 'recycled_funds'))

  // 5. Distribution metrics (no sent/OT filter)
  const INTEREST_INVESTMENT_TYPES = ['Convertible Note', 'Loan', 'Safe Note']

  const allDistributions = computed(() => transfers.value.filter((t) => t.type === 'distribution'))
  const paidDistributions = computed(() => allDistributions.value.filter((t) => t.status === 'paid'))
  const unpaidDistributions = computed(() => allDistributions.value.filter((t) => t.status !== 'paid'))

  const unpaidDistributedGross = computed(() => sumField(unpaidDistributions.value, 'amount'))
  const distributedGross = computed(() => sumField(paidDistributions.value, 'amount'))
  const distributedCarriedInterest = computed(() => sumField(paidDistributions.value, 'fee_amount'))
  const distributedOtherFee = computed(() => sumField(paidDistributions.value, 'other_fee_amount'))
  const distributedPreferredReturn = computed(() => sumField(paidDistributions.value, 'preferred_return_amount'))
  const distributedInterest = computed(() =>
    sumField(
      paidDistributions.value.filter((t) => INTEREST_INVESTMENT_TYPES.includes(t.investment_type)),
      'profit_amount',
    ),
  )
  const distributedPrincipal = computed(() =>
    sumField(
      paidDistributions.value.filter((t) => INTEREST_INVESTMENT_TYPES.includes(t.investment_type)),
      'return_of_capital_amount',
    ),
  )
  const distributedProfit = computed(() =>
    sumField(
      paidDistributions.value.filter((t) => !INTEREST_INVESTMENT_TYPES.includes(t.investment_type)),
      'profit_amount',
    ),
  )
  const distributedReturnOfCapital = computed(
    () =>
      sumField(
        paidDistributions.value.filter((t) => !INTEREST_INVESTMENT_TYPES.includes(t.investment_type)),
        'return_of_capital_amount',
      ) + distributedPrincipal.value,
  )
  const distributedNet = computed(
    () => distributedGross.value - distributedCarriedInterest.value - distributedOtherFee.value,
  )

  // 6. Entity-derived chart values
  const commitmentRemaining = computed(() => toNumber(entity.value?.insights?.commitment_remaining || initialMoney))
  const hurdleRemaining = computed(() => toNumber(entity.value?.insights?.distributed_hurdle_remaining || initialMoney))
  const totalHurdle = computed(() => toNumber(entity.value?.insights?.distributed_total_hurdle || initialMoney))
  const investableCapital = computed(
    () =>
      (calledSettledTotal.value ?? 0) +
      (calledRecycledFunds.value ?? 0) -
      (calledManagementFees.value ?? 0) -
      (calledOtherFees.value ?? 0) -
      (calledOperatingExpense.value ?? 0) -
      (interestAllocationPaid.value ?? 0) +
      (interestAllocationReceived.value ?? 0),
  )

  return {
    calledManagementFees,
    calledOtherFees,
    calledOperatingExpense,
    calledSettledTotal,
    calledPendingTotal,
    calledTotal,
    calledOperatingIncome,
    calledRecycledFunds,
    interestAllocationPaid,
    interestAllocationReceived,
    unpaidDistributedGross,
    distributedCarriedInterest,
    distributedGross,
    distributedNet,
    distributedOtherFee,
    distributedProfit,
    distributedReturnOfCapital,
    distributedPreferredReturn,
    distributedInterest,
    commitmentRemaining,
    hurdleRemaining,
    totalHurdle,
    investableCapital,
  }
}

// Helpers

function sumAmount(items: any[], type: string): number {
  return items.filter((t) => t.type === type).reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)
}

function sumAmountWhere(items: any[], type: string, status: string): number {
  return items
    .filter((t) => t.type === type && t.status === status)
    .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)
}

function sumField(items: any[], field: string): number {
  return items.reduce((sum, t) => sum + (parseFloat(t[field]) || 0), 0)
}
